import {
  CorrectionAttemptResult,
  GameCorrections,
  GameFix,
  RecentEarnings,
  RepairQueue,
} from '../types/CorrectionModel';
import { getJson } from './Utils';

// The cross-game repair queue for a set of games, in spaced-repetition order. Open to guests.
async function getRepairQueue(
  gameIds: number[],
  minWeight: number,
  username?: string | null,
): Promise<RepairQueue> {
  const response = await fetch('/api/Corrections/queue', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      GameIds: gameIds,
      MinWeight: minWeight,
      Username: username != null && username !== '' ? username : null,
    }),
  });
  return await getJson<RepairQueue>(response);
}

// The viewer's mistakes and weighted score for one game. Open to guests (nothing saved). `username`
// sets the point of view; pass the active player filter.
async function getForGame(gameId: number, username?: string | null): Promise<GameCorrections> {
  const qs = username != null && username !== '' ? `?username=${encodeURIComponent(username)}` : '';
  const response = await fetch(`/api/Corrections/game/${gameId}${qs}`);
  return await getJson<GameCorrections>(response);
}

// One game's mistakes in play order, for the trainer.
async function getGameFix(gameId: number): Promise<GameFix> {
  const response = await fetch(`/api/Corrections/game/${gameId}/fix`);
  return await getJson<GameFix>(response);
}

// Move ids in this game the signed-in user has solved. [] for guests.
async function getSolved(gameId: number): Promise<number[]> {
  const response = await fetch(`/api/Corrections/game/${gameId}/solved`);
  return await getJson<number[]>(response);
}

// Records one repair attempt. Signed-in users only; callers check first.
async function recordAttempt(
  moveId: number,
  gameId: number,
  success: boolean,
  // The hint (which piece moves) was taken; the server halves the payout like the client does.
  hintUsed = false,
  // What the played move gave up, when graded here. A failure is charged this instead of the mistake's
  // swing; null falls back to the swing.
  dropPct: number | null = null,
): Promise<CorrectionAttemptResult> {
  const response = await fetch('/api/Corrections/attempt', {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      MoveId: moveId,
      GameId: gameId,
      Success: success,
      HintUsed: hintUsed,
      DropPct: dropPct,
    }),
  });
  return await getJson<CorrectionAttemptResult>(response);
}

// The last few money movements, for WalletMirror's popover. Guests get Authenticated=false and [].
async function getRecentEarnings(limit = 5): Promise<RecentEarnings> {
  const response = await fetch(`/api/Corrections/earnings?limit=${limit}`, {
    credentials: 'same-origin',
  });
  return await getJson<RecentEarnings>(response);
}

export default {
  getGameFix,
  getForGame,
  getSolved,
  getRepairQueue,
  recordAttempt,
  getRecentEarnings,
};
